import { createApi } from "@reduxjs/toolkit/query/react";
import { gql } from "graphql-request";
import customBaseQuery from "../../../utils/customBaseQuery";

export const UserGroupAuthoringApi = createApi({
  reducerPath: "UserGroupAuthoringApi",
  baseQuery: customBaseQuery,
  refetchOnFocus: true,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    createUserGroup: builder.mutation({
      query: (payload: any) => ({
        url: `/group`,
        method: "POST",
        body: payload
      }),
      extraOptions: { service: "Resource" }
    }),
    updateGroupData: builder.mutation({
      query: ({ payload, groupId }: any) => ({
        url: `/group/update/${groupId}`,
        method: "PUT",
        body: payload
      }),
      extraOptions: { service: "Resource" }
    }),
    fetchUserGroupsByOwner: builder.query({
      query: ({ ownerId, pageNumber, title, status }: any) => ({
        url: "/graphql",
        body: gql`
          query {
            getGroupByStakeholder(dtoRequest:{
                      stakeholder: {
                        owner: ["${ownerId}"]
                      },
                      status: ${status},
                      dateFilter: "ALL",
                      title: "${title}"
                    }, 
                    pageNumber:${pageNumber}, 
                    pageSize:10,
                    sortKey: "createdat",
                    sortOrder: "DESC") {
                          timestamp
                          error
                          data {
                            searchResult{
                            visibility
                              id
                              title
                              shortdescription
                              expirydate
                              count
                              stakeholder{
                                    owner
                                    reviewer
                                    observer
                                  }
                              searchtags{
                                defaultTag
                              }
                            dynamicgroup
                            createdby
                            createdat
                            updatedby
                            updatedat
                            status
                          },
              pageNumber
              pageSize
              totalCount
            }
          }
        } 
        `
      }),
      extraOptions: { service: "Resource" },
      serializeQueryArgs: ({ endpointName }) => {
        return { endpointName };
      },
      merge(currentCacheData, responseData, otherArgs) {
        if (otherArgs.arg?.pageNumber > 1) {
          const messageMap = new Map();
          [
            ...currentCacheData.getGroupByStakeholder.data.searchResult,
            ...responseData.getGroupByStakeholder.data.searchResult
          ].map((msg) => {
            if (!messageMap.has(msg.id)) {
              messageMap.set(msg.id, msg);
            }
          });
          const mergedResult = [...messageMap.values()];
          return {
            ...responseData,
            getGroupByStakeholder: {
              ...responseData.getGroupByStakeholder,
              data: {
                ...responseData.getGroupByStakeholder.data,
                searchResult: mergedResult
              }
            }
          };
        } else return responseData;
      }
    }),
    fetchUserGroupsById: builder.query({
      query: (groupId: any) => ({
        url: "/graphql",
        body: gql`
          query {
          getGroup(groupId: "${groupId}"){
              timestamp
              error
              data {
                  title
                  id
                  status
                  stakeHolder {
                          owner
                          reviewer
                          observer
                          }
                  createdat
                  updatedat
                  createdby 
                  updatedby
                  expiryDate
                  searchTags {
                    defaultTag
                  }
                }
          }}
        `
      }),
      extraOptions: { service: "Resource" }
    }),
    updateStatus: builder.mutation({
      query: ({ groupId, status }: any) => ({
        url: `/group/update/${groupId}/${status}`,
        method: "PUT"
        // body: payload,
      }),
      extraOptions: { service: "Resource" }
    }),
    getUserList: builder.query({
      query: ({
        groupId,
        pageNumber,
        query = "",
        pageCount = 10
      }: {
        groupId: string | undefined;
        pageNumber: number;
        query?: string;
        pageCount?: number;
      }) => ({
        url: "/graphql",
        body: gql`
          query {
            getUsersInGroup(
              groupId: "${groupId}"
              pageNumber: ${pageNumber}
              pageSize: ${pageCount}
              needGroupDetails:${true}
              checkAuthority: ${false}
              searchKey: [\"${query}\"]
            ) {
              timestamp
              error
              data {
            group{
                id
                title
            }
            activeUsers{
                id
                name{
                    firstName
                    lastName
                    fullName
                
                }
                email
                enabled
            }
            inactiveUsers{
                id
                name{
                    firstName
                    lastName
                    fullName
                }
                email
                enabled
            }
            pageNumber
            pageSize
            totalCount
            totalPages
    }
            }
          }
        `
      }),
      extraOptions: { service: "Resource" }
    }),
    addUsersToGroup: builder.mutation({
      query: ({ groupId, payload }: any) => ({
        url: `/group/${groupId}`,
        method: "POST",
        body: payload
      }),
      extraOptions: { service: "Resource" }
    }),
    deleteUsersFromGroup: builder.mutation({
      query: (payload: any) => ({
        url: `/group/remove`,
        method: "POST",
        body: payload
      }),
      extraOptions: { service: "Resource" }
    }),
    deleteGroup: builder.mutation({
      query: (groupId: any) => ({
        url: `/group/${groupId}`,
        method: "DELETE"
      }),
      extraOptions: { service: "Resource" }
    }),
    getGroupDetails: builder.query({
      query: (groupId: any) => ({
        url: `/group/${groupId}`,
        method: "GET"
      }),
      extraOptions: { service: "Resource" }
    }),
    cloneGroup: builder.mutation({
      query: ({ groupId, cloneUsers }: any) => ({
        url: `/group/clone/${groupId}`,
        method: "POST",
        params: {
          cloneUsers: `${cloneUsers}`
        }
      }),
      extraOptions: { service: "Resource" }
    }),
    groupExcelUpload: builder.mutation({
      query: ({
        action,
        groupId,
        body
      }: {
        action: "ADD" | "REMOVE";
        groupId: string | undefined;
        body: any;
      }) => ({
        url: `/group/excelUpload/${action}/${groupId}`,
        method: "POST",
        body: body
      }),
      extraOptions: { service: "Resource" }
    })
  })
});

export const {
  useCreateUserGroupMutation,
  useFetchUserGroupsByOwnerQuery,
  useFetchUserGroupsByIdQuery,
  useUpdateGroupDataMutation,
  useUpdateStatusMutation,
  useGetUserListQuery,
  useLazyGetUserListQuery,
  useAddUsersToGroupMutation,
  useDeleteUsersFromGroupMutation,
  useDeleteGroupMutation,
  useGetGroupDetailsQuery,
  useCloneGroupMutation,
  useGroupExcelUploadMutation
} = UserGroupAuthoringApi;
